import { navigate } from "gatsby"
import identity from "lodash/identity"
import { useCallback, useMemo, useState } from "react"
import { Field, useForm } from "react-final-form"

import {
  marketingClient,
  orderClient,
  useMutation,
  useQueryClient,
  userClient,
} from "@trueskin-web/apis"
import { InputControl } from "@trueskin-web/components"
import { useAuth } from "@trueskin-web/context"
import { required } from "@trueskin-web/functions"
import { getCountries, saveCountry } from "@trueskin-web/locales"
import { authService, storageService } from "@trueskin-web/services"
import i18next, { Routes, useTranslation } from "@trueskin-web/translations"

import useConfirm from "../../../hooks/use-confirm"
import usePreferredPreset from "../../../hooks/use-preferred-preset"

const countries = getCountries()
const supportedCountries = ["DE"]
const SWISS_COUNTRY_CODE = "CH"
const antiAgingPresetList = "main_anti_aging"

const getConfirmationDialogContent = (preferredPreset) => {
  const isAntiAging =
    preferredPreset === antiAgingPresetList &&
    authService.hasAntiAgingGoalChosen()

  return {
    title: isAntiAging
      ? i18next.t("Start.Shipping.countryChangeDialog.titleAntiAging")
      : i18next.t("Start.Shipping.countryChangeDialog.title"),
    description: isAntiAging
      ? i18next.t("Start.Shipping.countryChangeDialog.descriptionAntiAging")
      : i18next.t("Start.Shipping.countryChangeDialog.description"),
  }
}

const CountryInput = ({ disabled, error, onChange, className }) => {
  const { t } = useTranslation()

  return (
    <Field
      data-testid="web-portal-shipping-form-country"
      name="country"
      required
      validate={(value) => required(value, t("Generic.errors.countryRequired"))}
      parse={identity}
      disabled={disabled}
      label={t("Start.Questionnaire.country")}
      placeholder={t("Start.Questionnaire.country")}
      rounded
      className={className}
      render={({
        input: { onChange: inputChange, ...input },
        meta,
        ...props
      }) => (
        <InputControl
          as="select"
          size="md"
          onChange={(e) => onChange(e, inputChange)}
          meta={{
            ...meta,
            error: error?.message || meta.error,
          }}
          {...input}
          {...props}
        >
          <option value="" hidden disabled>
            {t("Start.Questionnaire.country")}
          </option>
          {countries.map((country) => (
            <option key={country.code} value={country.code}>
              {country.label}
            </option>
          ))}
        </InputControl>
      )}
    />
  )
}

const CountryConfirmationDialog = ({ component: Dialog, isSubmitting }) => {
  const { preferredPreset } = usePreferredPreset()

  const dialogContent = useMemo(
    () => getConfirmationDialogContent(preferredPreset),
    []
  )

  return (
    <Dialog
      data-testid="web-portal-change-package-dialog"
      sx={{ "&[data-reach-dialog-content]": { maxWidth: 480 } }}
      isLoading={isSubmitting}
      aria-label="Change Country Dialog"
      noMobileFullscreen
      title={dialogContent.title}
      description={dialogContent.description}
      primaryActionLabel={i18next.t("Button.goBack")}
    />
  )
}

const Country = ({ disabled, className }) => {
  const form = useForm()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [ConfirmationDialog, confirmCountryChange, closeDialog] = useConfirm()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { mutateAsync: setCurrentPreset } = useMutation(
    orderClient.setCurrentPreset
  )

  const { mutateAsync: updateCountry, error: updateCountryError } = useMutation(
    userClient.setCountry,
    {
      onSuccess: async () => {
        await Promise.all([
          queryClient.refetchQueries("currentUser"),
          queryClient.refetchQueries("currentOrderSummary"),
        ])

        storageService.removeItem({
          id: storageService.getStorageKeys().USER_DETAILS,
        })

        const { preferredPresetForInitialPurchase } =
          queryClient.getQueryData("currentUser")

        const { data: presets } = await queryClient.fetchQuery(
          "currentPresets",
          () =>
            orderClient.getCurrentPresets(
              preferredPresetForInitialPurchase.preset
            )
        )

        const recommendedPresetId = presets.find(
          ({ isRecommended }) => isRecommended
        )?.id

        if (recommendedPresetId) {
          await setCurrentPreset(recommendedPresetId)
        }
      },
      onSettled: () => {
        setIsSubmitting(false)
        closeDialog()
      },
    }
  )

  const handleChange = useCallback(
    async (code) => {
      setIsSubmitting(true)
      await updateCountry(code)
      form.change("country", code)
      saveCountry(code)
      navigate(Routes.App.Start.ChoosePlan.url, { replace: true })
    },
    [form]
  )

  const validateChange = useCallback(
    async (e, callback) => {
      const selectedCode = e.target.value
      const previousCode = form.getState().values.country

      if (
        supportedCountries.includes(selectedCode) ||
        user.country === selectedCode
      ) {
        callback(e)
        return
      }

      if (selectedCode === SWISS_COUNTRY_CODE) {
        marketingClient.sendEvent({
          name: marketingClient.EVENT_TYPES_ENUM.SWISS_ERROR_2M,
        })

        const confirmed = await confirmCountryChange()

        const eventName = confirmed
          ? marketingClient.EVENT_TYPES_ENUM.SWISS_ERROR_CLICK_ON_BACK
          : marketingClient.EVENT_TYPES_ENUM.SWISS_ERROR_CLICK_ON_CANCEL

        marketingClient.sendEvent({ name: eventName })

        if (confirmed) {
          await handleChange(selectedCode)
        } else {
          form.change("country", previousCode)
        }
      } else {
        callback(e)
      }
    },
    [confirmCountryChange, form, handleChange]
  )

  return (
    <>
      <CountryInput
        disabled={disabled}
        error={updateCountryError}
        onChange={validateChange}
        className={className}
      />
      <CountryConfirmationDialog
        component={ConfirmationDialog}
        isSubmitting={isSubmitting}
      />
    </>
  )
}

export default Country
